import React, { useState, useRef, useEffect } from 'react';

// Define types for our component
interface JsonToSvgProps {}

// SVG theme options
type SvgTheme = 'light' | 'dark' | 'colorful' | 'minimal';

// Layout options
type LayoutOption = 'tree' | 'circular' | 'radial';

// Direction options (for tree layout)
type DirectionOption = 'top-down' | 'bottom-up' | 'left-right' | 'right-left';

// Node class for tree representation
class TreeNode {
  id: string;
  label: string;
  children: TreeNode[] = [];
  value: any;
  collapsed: boolean = false;
  x: number = 0;
  y: number = 0;
  width: number = 120;
  height: number = 40;
  type: 'object' | 'array' | 'primitive';
  
  constructor(label: string, value: any, type: 'object' | 'array' | 'primitive', id: string) {
    this.label = label;
    this.value = value;
    this.type = type;
    this.id = id;
  }
}

const JsonToSvg: React.FC<JsonToSvgProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the SVG output
  const [svgOutput, setSvgOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('json_visualization');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for SVG theme
  const [theme, setTheme] = useState<SvgTheme>('light');
  // State for layout option
  const [layoutOption, setLayoutOption] = useState<LayoutOption>('tree');
  // State for direction option
  const [directionOption, setDirectionOption] = useState<DirectionOption>('top-down');
  // State to track if the conversion happened
  const [isSvgGenerated, setIsSvgGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for SVG preview
  const [showPreview, setShowPreview] = useState<boolean>(true);
  // Tree data structure (root node)
  const [treeRoot, setTreeRoot] = useState<TreeNode | null>(null);
  // SVG dimensions
  const [svgWidth, setSvgWidth] = useState<number>(800);
  const [svgHeight, setSvgHeight] = useState<number>(600);
  // Node spacing
  const [nodeSpacing, setNodeSpacing] = useState<number>(60);
  // Level spacing
  const [levelSpacing, setLevelSpacing] = useState<number>(100);

  // Sample data for example
  const sampleData = {
    "name": "Project",
    "description": "JSON Visualization Example",
    "version": 1.0,
    "settings": {
      "theme": "dark",
      "debug": false,
      "limits": {
        "maxDepth": 10,
        "maxNodes": 100
      }
    },
    "components": [
      {
        "id": "component1",
        "type": "container",
        "visible": true,
        "children": [
          {
            "id": "child1",
            "type": "button",
            "text": "Click me"
          },
          {
            "id": "child2",
            "type": "input",
            "placeholder": "Enter text"
          }
        ]
      },
      {
        "id": "component2",
        "type": "panel",
        "visible": false
      }
    ],
    "stats": {
      "created": "2023-01-15",
      "updated": "2023-05-20",
      "views": 1250
    }
  };

  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsSvgGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsSvgGenerated(false);
    setSvgOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setSvgOutput('');
    setError('');
    setFileName('json_visualization');
    setTheme('light');
    setLayoutOption('tree');
    setDirectionOption('top-down');
    setIsSvgGenerated(false);
    setTreeRoot(null);
  };

  // Get colors for the selected theme
  const getThemeColors = (): { 
    background: string; 
    text: string; 
    line: string; 
    objectNode: string; 
    arrayNode: string; 
    primitiveNode: string;
    nodeStroke: string;
  } => {
    switch (theme) {
      case 'dark':
        return {
          background: '#1a1a1a',
          text: '#f5f5f5',
          line: '#6c757d',
          objectNode: '#4b778d',
          arrayNode: '#8b7d6b',
          primitiveNode: '#7d8b6b',
          nodeStroke: '#f8f9fa'
        };
      case 'colorful':
        return {
          background: '#f8f9fa',
          text: '#212529',
          line: '#6c757d',
          objectNode: '#8dd3c7',
          arrayNode: '#ffffb3',
          primitiveNode: '#bebada',
          nodeStroke: '#333333'
        };
      case 'minimal':
        return {
          background: '#ffffff',
          text: '#333333',
          line: '#cccccc',
          objectNode: '#f1f3f5',
          arrayNode: '#f1f3f5',
          primitiveNode: '#f1f3f5',
          nodeStroke: '#333333'
        };
      default: // light theme
        return {
          background: '#ffffff',
          text: '#333333',
          line: '#6c757d',
          objectNode: '#e9f5f8',
          arrayNode: '#f8f0e9',
          primitiveNode: '#f5f5f5',
          nodeStroke: '#333333'
        };
    }
  };

  // Convert JSON to tree structure
  const jsonToTree = (json: any, label: string = 'root', path: string = '0'): TreeNode => {
    let nodeType: 'object' | 'array' | 'primitive';
    
    if (json === null) {
      nodeType = 'primitive';
      const node = new TreeNode(label, 'null', nodeType, path);
      return node;
    }
    
    if (Array.isArray(json)) {
      nodeType = 'array';
      const node = new TreeNode(label, `Array[${json.length}]`, nodeType, path);
      
      json.forEach((item, index) => {
        const childNode = jsonToTree(item, `[${index}]`, `${path}-${index}`);
        node.children.push(childNode);
      });
      
      return node;
    }
    
    if (typeof json === 'object') {
      nodeType = 'object';
      const node = new TreeNode(label, `Object`, nodeType, path);
      
      Object.keys(json).forEach((key, index) => {
        const childNode = jsonToTree(json[key], key, `${path}-${key}`);
        node.children.push(childNode);
      });
      
      return node;
    }
    
    // Handle primitive values
    nodeType = 'primitive';
    const valueStr = typeof json === 'string' ? `"${json}"` : String(json);
    const node = new TreeNode(label, valueStr, nodeType, path);
    return node;
  };

  // Calculate positions for nodes based on layout option
  const calculatePositions = (node: TreeNode, rootX: number = svgWidth / 2, rootY: number = 100): void => {
    if (layoutOption === 'tree') {
      calculateTreePositions(node, rootX, rootY);
    } else if (layoutOption === 'circular') {
      calculateCircularPositions(node, svgWidth / 2, svgHeight / 2);
    } else if (layoutOption === 'radial') {
      calculateRadialPositions(node, svgWidth / 2, svgHeight / 2);
    }
  };

  // Calculate positions for tree layout
  const calculateTreePositions = (node: TreeNode, x: number = svgWidth / 2, y: number = 100, level: number = 0): void => {
    let dx = 0;
    let dy = 0;
    
    // Set direction based on user selection
    switch (directionOption) {
      case 'top-down':
        dx = 0;
        dy = 1;
        break;
      case 'bottom-up':
        dx = 0;
        dy = -1;
        y = svgHeight - 100;
        break;
      case 'left-right':
        dx = 1;
        dy = 0;
        x = 100;
        break;
      case 'right-left':
        dx = -1;
        dy = 0;
        x = svgWidth - 100;
        break;
    }
    
    // Skip collapsed nodes
    if (node.children.length === 0 || node.collapsed) {
      node.x = x;
      node.y = y;
      return;
    }
    
    // Set visible children
    const visibleChildren = node.children;
    
    // Initialize variables for child positioning
    const isHorizontal = dx !== 0;
    const nodeSize = isHorizontal ? node.height : node.width;
    const levelStep = levelSpacing;
    const siblingStep = nodeSpacing + nodeSize;
    
    // Calculate total width needed for children
    const totalChildrenSpace = (visibleChildren.length - 1) * siblingStep;
    
    // Position each child
    let childPos = isHorizontal ? 
      y - totalChildrenSpace / 2 : 
      x - totalChildrenSpace / 2;
    
    visibleChildren.forEach(child => {
      const childX = isHorizontal ? 
        x + dx * levelStep : 
        childPos;
      
      const childY = isHorizontal ? 
        childPos : 
        y + dy * levelStep;
      
      // Recursively position the child's children
      calculateTreePositions(
        child, 
        childX, 
        childY, 
        level + 1
      );
      
      childPos += siblingStep;
    });
    
    // Position the current node
    if (visibleChildren.length > 0) {
      if (isHorizontal) {
        // For horizontal layouts, center the node vertically
        const firstChild = visibleChildren[0];
        const lastChild = visibleChildren[visibleChildren.length - 1];
        node.y = (firstChild.y + lastChild.y) / 2;
        node.x = x;
      } else {
        // For vertical layouts, center the node horizontally
        const firstChild = visibleChildren[0];
        const lastChild = visibleChildren[visibleChildren.length - 1];
        node.x = (firstChild.x + lastChild.x) / 2;
        node.y = y;
      }
    } else {
      node.x = x;
      node.y = y;
    }
  };

  // Calculate positions for circular layout
  const calculateCircularPositions = (node: TreeNode, centerX: number, centerY: number): void => {
    const radius = Math.min(svgWidth, svgHeight) * 0.35;
    const calculateLevel = (currNode: TreeNode, level: number = 0, angle: number = 0, angleSpan: number = 2 * Math.PI) => {
      // Position current node
      const r = level === 0 ? 0 : radius * (level / 5);
      currNode.x = centerX + r * Math.cos(angle);
      currNode.y = centerY + r * Math.sin(angle);
      
      // Skip children if collapsed
      if (currNode.collapsed || currNode.children.length === 0) {
        return;
      }
      
      // Position children
      const childCount = currNode.children.length;
      const angleStep = angleSpan / childCount;
      
      currNode.children.forEach((child, index) => {
        const childAngle = angle - (angleSpan / 2) + (index + 0.5) * angleStep;
        calculateLevel(child, level + 1, childAngle, angleStep);
      });
    };
    
    // Start from the root
    calculateLevel(node);
  };

  // Calculate positions for radial layout
  const calculateRadialPositions = (node: TreeNode, centerX: number, centerY: number): void => {
    // First, count the total number of leaf nodes
    const countLeaves = (currNode: TreeNode): number => {
      if (currNode.collapsed || currNode.children.length === 0) {
        return 1;
      }
      return currNode.children.reduce((acc, child) => acc + countLeaves(child), 0);
    };
    
    const totalLeaves = countLeaves(node);
    let leafIndex = 0;
    
    const calculatePositions = (currNode: TreeNode, level: number = 0, angle: number = 0) => {
      // Calculate radius based on level
      const radius = level === 0 ? 0 : level * levelSpacing;
      
      // Position current node
      currNode.x = centerX + radius * Math.cos(angle);
      currNode.y = centerY + radius * Math.sin(angle);
      
      // Skip children if collapsed
      if (currNode.collapsed || currNode.children.length === 0) {
        leafIndex++;
        return;
      }
      
      // Calculate positions for children
      const childCount = currNode.children.length;
      let childLeafStart = leafIndex;
      
      currNode.children.forEach(child => {
        const childLeaves = countLeaves(child);
        const childAngle = Math.PI * 2 * (childLeafStart + childLeaves / 2) / totalLeaves;
        calculatePositions(child, level + 1, childAngle);
        childLeafStart += childLeaves;
      });
    };
    
    // Start from the root
    calculatePositions(node);
  };

  // Generate SVG based on the tree structure
  const generateSvg = (root: TreeNode): string => {
    const colors = getThemeColors();
    const SVG_PADDING = 40;
    
    // Find the bounds of the tree
    let minX = Number.MAX_SAFE_INTEGER;
    let minY = Number.MAX_SAFE_INTEGER;
    let maxX = 0;
    let maxY = 0;
    
    const traverse = (node: TreeNode) => {
      minX = Math.min(minX, node.x - node.width / 2);
      minY = Math.min(minY, node.y - node.height / 2);
      maxX = Math.max(maxX, node.x + node.width / 2);
      maxY = Math.max(maxY, node.y + node.height / 2);
      
      if (!node.collapsed) {
        node.children.forEach(child => traverse(child));
      }
    };
    
    traverse(root);
    
    // Add padding
    minX -= SVG_PADDING;
    minY -= SVG_PADDING;
    maxX += SVG_PADDING;
    maxY += SVG_PADDING;
    
    const width = maxX - minX;
    const height = maxY - minY;
    
    // Start SVG
    let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="${minX} ${minY} ${width} ${height}">`;
    
    // Add a background
    svg += `<rect x="${minX}" y="${minY}" width="${width}" height="${height}" fill="${colors.background}" />`;
    
    // Add definitions for markers (arrow heads)
    svg += `
      <defs>
        <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" 
          markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="${colors.line}" />
        </marker>
      </defs>
    `;
    
    // Function to add edges (connections between nodes)
    const addEdges = (node: TreeNode) => {
      if (node.collapsed || node.children.length === 0) {
        return;
      }
      
      node.children.forEach(child => {
        // Calculate start and end points based on layout direction
        let startX = node.x;
        let startY = node.y;
        let endX = child.x;
        let endY = child.y;
        
        // Adjust the connection points based on layout direction
        if (directionOption === 'top-down') {
          startY = node.y + node.height / 2;
          endY = child.y - child.height / 2;
        } else if (directionOption === 'bottom-up') {
          startY = node.y - node.height / 2;
          endY = child.y + child.height / 2;
        } else if (directionOption === 'left-right') {
          startX = node.x + node.width / 2;
          endX = child.x - child.width / 2;
        } else if (directionOption === 'right-left') {
          startX = node.x - node.width / 2;
          endX = child.x + child.width / 2;
        }
        
        // For circular and radial layouts, draw straight lines from center to center
        if (layoutOption === 'circular' || layoutOption === 'radial') {
          startX = node.x;
          startY = node.y;
          endX = child.x;
          endY = child.y;
        }
        
        // Draw the line with an arrow
        svg += `<line x1="${startX}" y1="${startY}" x2="${endX}" y2="${endY}" 
          stroke="${colors.line}" stroke-width="1.5" marker-end="url(#arrow)" />`;
        
        // Recursively add edges for the child's children
        addEdges(child);
      });
    };
    
    // Function to add nodes
    const addNodes = (node: TreeNode) => {
      // Determine node color based on type
      let nodeColor = '';
      if (node.type === 'object') nodeColor = colors.objectNode;
      else if (node.type === 'array') nodeColor = colors.arrayNode;
      else nodeColor = colors.primitiveNode;
      
      // Calculate the node's position
      const x = node.x - node.width / 2;
      const y = node.y - node.height / 2;
      
      // Add rectangle for node
      svg += `<rect x="${x}" y="${y}" width="${node.width}" height="${node.height}" 
        fill="${nodeColor}" stroke="${colors.nodeStroke}" rx="5" />`;
      
      // Add label text
      svg += `<text x="${node.x}" y="${node.y}" font-family="Arial" font-size="12" 
        text-anchor="middle" dominant-baseline="middle" fill="${colors.text}" font-weight="bold">${node.label}</text>`;
      
      // Add value text (if primitive or summary info)
      if (node.type === 'primitive') {
        const textY = node.y + node.height / 4;
        let valueText = String(node.value);
        if (valueText.length > 15) valueText = valueText.substring(0, 12) + '...';
        
        svg += `<text x="${node.x}" y="${textY}" font-family="Arial" font-size="10" 
          text-anchor="middle" fill="${colors.text}">${valueText}</text>`;
      }
      
      // Add collapse/expand button if node has children
      if (node.children.length > 0) {
        const btnX = x + node.width - 15;
        const btnY = y + 15;
        const btnRadius = 8;
        
        svg += `<circle cx="${btnX}" cy="${btnY}" r="${btnRadius}" fill="#fff" stroke="${colors.line}" 
          class="toggle-btn" data-node-id="${node.id}" />`;
        
        // Add + or - symbol
        if (node.collapsed) {
          svg += `<text x="${btnX}" y="${btnY}" font-family="Arial" font-size="12" 
            text-anchor="middle" dominant-baseline="middle" class="toggle-text" data-node-id="${node.id}">+</text>`;
        } else {
          svg += `<text x="${btnX}" y="${btnY}" font-family="Arial" font-size="12" 
            text-anchor="middle" dominant-baseline="middle" class="toggle-text" data-node-id="${node.id}">-</text>`;
        }
      }
      
      // Recursively add child nodes
      if (!node.collapsed) {
        node.children.forEach(child => addNodes(child));
      }
    };
    
    // First draw all edges (so they appear behind nodes)
    addEdges(root);
    
    // Then draw all nodes
    addNodes(root);
    
    // Add JavaScript for interactivity
    svg += `
      <script type="text/javascript"><![CDATA[
        document.addEventListener('DOMContentLoaded', function() {
          // Get all toggle buttons
          const toggleBtns = document.querySelectorAll('.toggle-btn, .toggle-text');
          
          // Add click event to each button
          toggleBtns.forEach(function(btn) {
            btn.addEventListener('click', function() {
              // Get the node ID
              const nodeId = this.getAttribute('data-node-id');
              
              // Send a message to the parent window
              window.parent.postMessage({
                action: 'toggleNode',
                nodeId: nodeId
              }, '*');
            });
          });
        });
      ]]></script>
    `;
    
    // Close SVG
    svg += '</svg>';
    
    return svg;
  };

  // Toggle node collapsed state
  const toggleNodeCollapse = (nodeId: string) => {
    if (!treeRoot) return;
    
    // Function to find and toggle a node
    const toggleNode = (node: TreeNode): boolean => {
      if (node.id === nodeId) {
        node.collapsed = !node.collapsed;
        return true;
      }
      
      for (const child of node.children) {
        if (toggleNode(child)) return true;
      }
      
      return false;
    };
    
    // Create a copy of the tree root to modify
    const newRoot = { ...treeRoot };
    
    // Find and toggle the node
    if (toggleNode(newRoot)) {
      // Recalculate positions
      calculatePositions(newRoot);
      setTreeRoot(newRoot);
      
      // Regenerate SVG
      const newSvg = generateSvg(newRoot);
      setSvgOutput(newSvg);
    }
  };

  // Listen for messages from the SVG iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.action === 'toggleNode') {
        toggleNodeCollapse(event.data.nodeId);
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [treeRoot]);

  // Convert JSON to SVG
  const convertJsonToSvg = () => {
    setError('');
    setIsLoading(true);
    setIsSvgGenerated(false);
    setSvgOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      
      // Convert JSON to tree structure
      const root = jsonToTree(jsonData);
      
      // Calculate positions based on layout type
      calculatePositions(root);
      
      // Store the tree for later use
      setTreeRoot(root);
      
      // Generate SVG from the tree
      const svgContent = generateSvg(root);
      
      setSvgOutput(svgContent);
      setIsSvgGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating SVG: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy SVG to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(svgOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download SVG file
  const downloadSvg = () => {
    const blob = new Blob([svgOutput], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'json_visualization'}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to SVG Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to interactive SVG visualizations. Paste your JSON data below to generate a visual representation of your data structure.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="theme" className="block text-sm font-medium text-gray-700 mb-1">
                    SVG Theme
                  </label>
                  <select
                    id="theme"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={theme}
                    onChange={(e) => setTheme(e.target.value as SvgTheme)}
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="colorful">Colorful</option>
                    <option value="minimal">Minimal</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="layoutOption" className="block text-sm font-medium text-gray-700 mb-1">
                    Layout Style
                  </label>
                  <select
                    id="layoutOption"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={layoutOption}
                    onChange={(e) => setLayoutOption(e.target.value as LayoutOption)}
                  >
                    <option value="tree">Tree</option>
                    <option value="circular">Circular</option>
                    <option value="radial">Radial</option>
                  </select>
                </div>
                
                {layoutOption === 'tree' && (
                  <div>
                    <label htmlFor="directionOption" className="block text-sm font-medium text-gray-700 mb-1">
                      Direction
                    </label>
                    <select
                      id="directionOption"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      value={directionOption}
                      onChange={(e) => setDirectionOption(e.target.value as DirectionOption)}
                    >
                      <option value="top-down">Top-Down</option>
                      <option value="bottom-up">Bottom-Up</option>
                      <option value="left-right">Left-Right</option>
                      <option value="right-left">Right-Left</option>
                    </select>
                  </div>
                )}
                
                <div>
                  <label htmlFor="nodeSpacing" className="block text-sm font-medium text-gray-700 mb-1">
                    Node Spacing
                  </label>
                  <input
                    type="range"
                    id="nodeSpacing"
                    className="w-full"
                    min="20"
                    max="120"
                    step="5"
                    value={nodeSpacing}
                    onChange={(e) => setNodeSpacing(parseInt(e.target.value))}
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Compact</span>
                    <span>Spacious</span>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="levelSpacing" className="block text-sm font-medium text-gray-700 mb-1">
                    Level Spacing
                  </label>
                  <input
                    type="range"
                    id="levelSpacing"
                    className="w-full"
                    min="50"
                    max="200"
                    step="10"
                    value={levelSpacing}
                    onChange={(e) => setLevelSpacing(parseInt(e.target.value))}
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Compact</span>
                    <span>Spacious</span>
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showPreview"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={showPreview}
                    onChange={(e) => setShowPreview(e.target.checked)}
                  />
                  <label htmlFor="showPreview" className="ml-2 block text-sm text-gray-700">
                    Show Preview
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToSvg}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to SVG'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              SVG Output
            </label>
            
            {isSvgGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy SVG'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadSvg}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isSvgGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 3v4M8 3v4M3 9h18M8 15h.01M12 15h.01M16 15h.01M8 19h.01M12 19h.01M16 19h.01" />
                  </svg>
                  <p>
                    SVG visualization will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isSvgGenerated && (
              <div className="flex-grow flex flex-col">
                {showPreview ? (
                  <div className="mb-3 flex-grow">
                    <div className="mb-2 flex justify-between items-center">
                      <h3 className="font-medium text-gray-800">Preview:</h3>
                      <button
                        className="text-xs text-gray-600 hover:text-gray-800"
                        onClick={() => setShowPreview(false)}
                      >
                        Hide Preview
                      </button>
                    </div>
                    <div className="border border-gray-200 rounded-md overflow-auto p-3 mb-3 flex-grow" style={{ height: '400px' }}>
                      <iframe
                        srcDoc={svgOutput}
                        title="SVG Preview"
                        className="w-full h-full"
                        sandbox="allow-scripts"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="mb-3">
                    <button
                      className="text-xs text-blue-600 hover:text-blue-800"
                      onClick={() => setShowPreview(true)}
                    >
                      Show Preview
                    </button>
                  </div>
                )}
                
                <div className="flex-grow flex flex-col">
                  <label htmlFor="svgOutput" className="text-sm font-medium text-gray-700 mb-1">
                    SVG Code:
                  </label>
                  <textarea
                    id="svgOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-xs focus:ring-blue-500 focus:border-blue-500"
                    value={svgOutput}
                    readOnly
                    style={{ minHeight: '200px' }}
                  />
                </div>
              </div>
            )}
          </div>
          
          {isSvgGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">SVG Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The generated SVG includes interactive features - users can click on nodes to expand or collapse nested content.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to SVG Visualization</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Visualizer Works</h3>
            <p className="text-gray-600">
              This tool transforms your JSON data into interactive SVG visualizations. The converter preserves your JSON structure
              while creating a visual representation that makes it easier to understand complex data relationships.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Multiple Layouts</strong> - Choose from tree, circular, or radial layouts</li>
              <li><strong>Interactive Visualization</strong> - Expand or collapse nodes to focus on relevant parts</li>
              <li><strong>Customizable Appearance</strong> - Select from different themes and spacing options</li>
              <li><strong>Multiple Direction Options</strong> - For tree layouts, select the flow direction</li>
              <li><strong>Downloadable SVG</strong> - Save your visualization for use in documentation or presentations</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Visualizations</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON data before conversion</li>
              <li>For large JSON structures, the "Tree" layout with "Left-Right" direction often provides the most readable result</li>
              <li>Adjust the node spacing and level spacing to optimize the visualization for your data size</li>
              <li>The "Circular" layout works well for hierarchical data with a clear parent-child relationship</li>
              <li>The generated SVG file can be opened in vector graphics editors for further customization</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToSvg;